import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import {
  Activity,
  AlertTriangle,
  Clock3,
  Download,
  GitBranch,
  History,
  Layers3,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Radar,
  RotateCcw,
  ShieldCheck,
  SkipBack,
  SkipForward,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import type { AiC2MobOverlay, MobEdge, MobNode, MobSnapshot, MobSnapshotSummary } from './MainOperatingBaseExplorer';

type ReplayMode = 'architecture' | 'attack';
type ReplaySpeed = 0.5 | 1 | 2 | 4;
type ReplayEventKind = 'node_first_seen' | 'relationship_first_seen' | 'threat_seen' | 'node_last_seen' | 'relationship_last_seen';

type ReplayEvent = {
  id: string;
  timestamp: string;
  timeMs: number;
  kind: ReplayEventKind;
  label: string;
  nodeKeys: string[];
  edgeKeys: string[];
  severity: 'info' | 'medium' | 'high' | 'critical';
  sourceTables: string[];
};

type AiCaseEvent = {
  time: string;
  text: string;
  category: string;
  nodeKeys: string[];
};

type LayoutNode = MobNode & { x: number; y: number; width: number; height: number };
type LayoutZone = { id: string; label: string; x: number; y: number; width: number; height: number };

type SnapshotDiff = {
  newNodes: Set<string>;
  removedNodes: Set<string>;
  changedNodes: Set<string>;
  newEdges: Set<string>;
  removedEdges: Set<string>;
  changedEdges: Set<string>;
};

const WIDTH = 1600;
const HEIGHT = 820;
const ZONE_GEOMETRY: Record<string, { x: number; y: number; width: number; height: number }> = {
  identity: { x: 28, y: 38, width: 350, height: 330 },
  azure: { x: 400, y: 38, width: 510, height: 370 },
  security: { x: 932, y: 38, width: 300, height: 370 },
  threat: { x: 1254, y: 38, width: 320, height: 370 },
  onprem: { x: 28, y: 440, width: 510, height: 340 },
  telemetry: { x: 560, y: 440, width: 410, height: 340 },
  external: { x: 992, y: 440, width: 582, height: 340 },
};

function normalize(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function n(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
}

function parseTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function riskClass(score: number): string {
  if (score >= 85) return 'text-rose-300';
  if (score >= 65) return 'text-orange-300';
  if (score >= 35) return 'text-amber-300';
  return 'text-emerald-300';
}

function diffSnapshots(current: MobSnapshot, previous: MobSnapshot | null): SnapshotDiff | null {
  if (!previous) return null;
  const currentNodes = new globalThis.Map(current.nodes.map((node) => [node.key, node]));
  const previousNodes = new globalThis.Map(previous.nodes.map((node) => [node.key, node]));
  const currentEdges = new globalThis.Map(current.edges.map((edge) => [edge.key, edge]));
  const previousEdges = new globalThis.Map(previous.edges.map((edge) => [edge.key, edge]));
  const newNodes = new Set<string>();
  const removedNodes = new Set<string>();
  const changedNodes = new Set<string>();
  const newEdges = new Set<string>();
  const removedEdges = new Set<string>();
  const changedEdges = new Set<string>();

  for (const [key, node] of currentNodes) {
    const prior = previousNodes.get(key);
    if (!prior) { newNodes.add(key); continue; }
    if (
      node.risk.score !== prior.risk.score ||
      node.monitoring.status !== prior.monitoring.status ||
      node.monitoring.coveragePercent !== prior.monitoring.coveragePercent ||
      node.threatState !== prior.threatState ||
      node.crownJewel !== prior.crownJewel ||
      node.criticality !== prior.criticality
    ) changedNodes.add(key);
  }
  for (const key of previousNodes.keys()) if (!currentNodes.has(key)) removedNodes.add(key);

  for (const [key, edge] of currentEdges) {
    const prior = previousEdges.get(key);
    if (!prior) { newEdges.add(key); continue; }
    if (
      edge.evidenceCount !== prior.evidenceCount ||
      edge.confidence !== prior.confidence ||
      edge.provenance !== prior.provenance ||
      edge.lastSeen !== prior.lastSeen
    ) changedEdges.add(key);
  }
  for (const key of previousEdges.keys()) if (!currentEdges.has(key)) removedEdges.add(key);

  return { newNodes, removedNodes, changedNodes, newEdges, removedEdges, changedEdges };
}

function eventSeverity(node?: MobNode, edge?: MobEdge): ReplayEvent['severity'] {
  if (node?.threatState === 'active_threat' || (node?.risk.score ?? 0) >= 85) return 'critical';
  if ((node?.risk.score ?? 0) >= 65 || edge?.type.includes('ioc') || edge?.type.includes('alert')) return 'high';
  if ((node?.risk.score ?? 0) >= 35 || (edge?.evidenceCount ?? 0) >= 5) return 'medium';
  return 'info';
}

function replayEvents(snapshot: MobSnapshot): ReplayEvent[] {
  const rows: ReplayEvent[] = [];
  const seen = new Set<string>();
  const add = (event: ReplayEvent) => {
    const key = `${event.kind}|${event.timestamp}|${event.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(event);
  };

  for (const node of snapshot.nodes) {
    const firstMs = parseTime(node.firstSeen);
    if (firstMs !== null && node.firstSeen) {
      const kind: ReplayEventKind = node.threatState !== 'normal' ? 'threat_seen' : 'node_first_seen';
      add({
        id: `node-first:${node.key}`,
        timestamp: node.firstSeen,
        timeMs: firstMs,
        kind,
        label: `${node.label} ${kind === 'threat_seen' ? 'entered threat/exposure context' : 'was first observed in the architecture'}.`,
        nodeKeys: [node.key],
        edgeKeys: [],
        severity: eventSeverity(node),
        sourceTables: node.sourceTables,
      });
    }
    const lastMs = parseTime(node.lastSeen);
    if (lastMs !== null && node.lastSeen && node.lastSeen !== node.firstSeen && node.threatState !== 'normal') {
      add({
        id: `node-last:${node.key}`,
        timestamp: node.lastSeen,
        timeMs: lastMs,
        kind: 'node_last_seen',
        label: `${node.label} recorded its latest retained threat/exposure activity.`,
        nodeKeys: [node.key],
        edgeKeys: [],
        severity: eventSeverity(node),
        sourceTables: node.sourceTables,
      });
    }
  }

  for (const edge of snapshot.edges) {
    const firstMs = parseTime(edge.firstSeen);
    if (firstMs !== null && edge.firstSeen) {
      add({
        id: `edge-first:${edge.key}`,
        timestamp: edge.firstSeen,
        timeMs: firstMs,
        kind: 'relationship_first_seen',
        label: `${edge.label} relationship was first observed.`,
        nodeKeys: [edge.from, edge.to],
        edgeKeys: [edge.key],
        severity: eventSeverity(undefined, edge),
        sourceTables: edge.sourceTables,
      });
    }
    const lastMs = parseTime(edge.lastSeen);
    if (lastMs !== null && edge.lastSeen && edge.lastSeen !== edge.firstSeen && edge.evidenceCount > 1) {
      add({
        id: `edge-last:${edge.key}`,
        timestamp: edge.lastSeen,
        timeMs: lastMs,
        kind: 'relationship_last_seen',
        label: `${edge.label} relationship recorded its latest retained activity.`,
        nodeKeys: [edge.from, edge.to],
        edgeKeys: [edge.key],
        severity: eventSeverity(undefined, edge),
        sourceTables: edge.sourceTables,
      });
    }
  }

  return rows
    .sort((a, b) => a.timeMs - b.timeMs || a.id.localeCompare(b.id))
    .slice(-800);
}

function aiCaseEvents(snapshot: MobSnapshot, overlay: AiC2MobOverlay | null): AiCaseEvent[] {
  if (!overlay?.timeline?.length) return [];
  return overlay.timeline.slice(0, 40).map((item) => {
    const text = normalize(item.text);
    const nodeKeys = snapshot.nodes
      .filter((node) => {
        const label = normalize(node.label);
        if (!label || label.length < 3) return false;
        return text.includes(label) || normalize(JSON.stringify(node.properties)).split(/\s+/).some((piece) => piece.length > 6 && text.includes(piece));
      })
      .slice(0, 12)
      .map((node) => node.key);
    return { time: item.time, text: item.text, category: item.category, nodeKeys };
  });
}

function layout(snapshot: MobSnapshot, priority: Set<string>): { nodes: LayoutNode[]; zones: LayoutZone[]; edges: MobEdge[] } {
  const degree = new globalThis.Map<string, number>();
  snapshot.edges.forEach((edge) => {
    degree.set(edge.from, (degree.get(edge.from) ?? 0) + Math.min(20, edge.evidenceCount || 1));
    degree.set(edge.to, (degree.get(edge.to) ?? 0) + Math.min(20, edge.evidenceCount || 1));
  });
  const nodes: LayoutNode[] = [];
  const zones: LayoutZone[] = [];
  const visible = new Set<string>();

  for (const zone of snapshot.zones) {
    const geometry = ZONE_GEOMETRY[zone.id];
    if (!geometry) continue;
    zones.push({ id: zone.id, label: zone.label, ...geometry });
    const rows = snapshot.nodes
      .filter((node) => node.zone === zone.id)
      .sort((a, b) => Number(priority.has(b.key)) - Number(priority.has(a.key)) || Number(b.crownJewel) - Number(a.crownJewel) || b.risk.score - a.risk.score || (degree.get(b.key) ?? 0) - (degree.get(a.key) ?? 0));
    const nodeWidth = geometry.width > 500 ? 142 : 126;
    const nodeHeight = 50;
    const gapX = 14;
    const gapY = 12;
    const cols = Math.max(1, Math.floor((geometry.width - 28) / (nodeWidth + gapX)));
    const rowsAvailable = Math.max(1, Math.floor((geometry.height - 76) / (nodeHeight + gapY)));
    const capacity = Math.min(rows.length, cols * rowsAvailable);
    rows.slice(0, capacity).forEach((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      nodes.push({
        ...node,
        x: geometry.x + 16 + nodeWidth / 2 + col * (nodeWidth + gapX),
        y: geometry.y + 66 + nodeHeight / 2 + row * (nodeHeight + gapY),
        width: nodeWidth,
        height: nodeHeight,
      });
      visible.add(node.key);
    });
  }

  const edges = snapshot.edges
    .filter((edge) => visible.has(edge.from) && visible.has(edge.to))
    .sort((a, b) => Number(priority.has(b.from) || priority.has(b.to)) - Number(priority.has(a.from) || priority.has(a.to)) || b.evidenceCount - a.evidenceCount)
    .slice(0, 320);
  return { nodes, zones, edges };
}

function curvedPath(from: LayoutNode, to: LayoutNode): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = -dy / distance;
  const ny = dx / distance;
  const bend = Math.min(82, Math.max(15, distance * .08)) * (from.y <= to.y ? 1 : -1);
  return `M ${from.x} ${from.y} Q ${(from.x + to.x) / 2 + nx * bend} ${(from.y + to.y) / 2 + ny * bend} ${to.x} ${to.y}`;
}

function downloadText(filename: string, body: string, type: string) {
  const blob = new Blob([body], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function MainOperatingBaseReplay({
  customerId,
  customerName,
  snapshot,
  snapshots,
  aiOverlay,
  onExit,
}: {
  customerId: string;
  customerName: string;
  snapshot: MobSnapshot;
  snapshots: MobSnapshotSummary[];
  aiOverlay: AiC2MobOverlay | null;
  onExit: () => void;
}) {
  const { canCapability } = useAuth();
  const orderedFrames = useMemo(() => {
    const rows = snapshots.length ? [...snapshots] : [{ id: snapshot.id, sourceFobSnapshotId: snapshot.sourceFobSnapshotId, status: snapshot.status, generatedAt: snapshot.generatedAt, completedAt: snapshot.completedAt, summary: snapshot.summary }];
    return rows.sort((a, b) => (parseTime(a.generatedAt) ?? 0) - (parseTime(b.generatedAt) ?? 0));
  }, [snapshots, snapshot]);
  const foundInitialIndex = orderedFrames.findIndex((item) => item.id === snapshot.id);
  const initialIndex = foundInitialIndex >= 0 ? foundInitialIndex : Math.max(0, orderedFrames.length - 1);
  const [mode, setMode] = useState<ReplayMode>('architecture');
  const [frameIndex, setFrameIndex] = useState(initialIndex);
  const [frame, setFrame] = useState<MobSnapshot>(snapshot);
  const [previousFrame, setPreviousFrame] = useState<MobSnapshot | null>(null);
  const [loadingFrame, setLoadingFrame] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<ReplaySpeed>(1);
  const [attackIndex, setAttackIndex] = useState(0);
  const [selectedAiIndex, setSelectedAiIndex] = useState<number | null>(null);
  const [selectedNodeKey, setSelectedNodeKey] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [threatOnly, setThreatOnly] = useState(false);
  const cardRef = useRef<HTMLElement | null>(null);
  const panRef = useRef({ active: false, pointerId: -1, clientX: 0, clientY: 0, panX: 0, panY: 0 });
  const cacheRef = useRef(new globalThis.Map<string, MobSnapshot>([[snapshot.id, snapshot]]));

  const canSnapshots = canCapability('main_operating_base', 'play_snapshot_replay');
  const canAttack = canCapability('main_operating_base', 'play_attack_replay');
  const canEvidence = canCapability('main_operating_base', 'view_replay_evidence');
  const canAiLane = canCapability('main_operating_base', 'view_ai_c2_replay_lane');
  const canExport = canCapability('main_operating_base', 'export_replay');

  async function audit(action: string, details: Record<string, unknown> = {}) {
    try {
      await apiFetch('/main-operating-base/interaction-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, snapshotId: frame.id, action, details }),
      });
    } catch { /* best effort */ }
  }

  useEffect(() => {
    void audit('mob_replay_opened', { initialSnapshotId: snapshot.id, availableFrames: orderedFrames.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onFullscreen = () => setFullscreen(document.fullscreenElement === cardRef.current);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => document.removeEventListener('fullscreenchange', onFullscreen);
  }, []);

  useEffect(() => {
    if (mode !== 'architecture' || !orderedFrames.length) return;
    let cancelled = false;
    const target = orderedFrames[Math.max(0, Math.min(frameIndex, orderedFrames.length - 1))];
    if (!target) return;
    const load = async () => {
      setLoadingFrame(true);
      try {
        let current = cacheRef.current.get(target.id);
        if (!current) {
          const data = await apiFetch<{ snapshot?: MobSnapshot | null }>(`/main-operating-base/snapshot/${encodeURIComponent(target.id)}?customerId=${encodeURIComponent(customerId)}`);
          current = data.snapshot ?? undefined;
          if (current) cacheRef.current.set(target.id, current);
        }
        let prior: MobSnapshot | null = null;
        if (frameIndex > 0) {
          const previous = orderedFrames[frameIndex - 1];
          if (previous) {
            prior = cacheRef.current.get(previous.id) ?? null;
            if (!prior) {
              const data = await apiFetch<{ snapshot?: MobSnapshot | null }>(`/main-operating-base/snapshot/${encodeURIComponent(previous.id)}?customerId=${encodeURIComponent(customerId)}`);
              prior = data.snapshot ?? null;
              if (prior) cacheRef.current.set(previous.id, prior);
            }
          }
        }
        if (!cancelled && current) {
          setFrame(current);
          setPreviousFrame(prior);
          setSelectedNodeKey('');
          void audit('mob_replay_frame_selected', { frameIndex, snapshotId: current.id, generatedAt: current.generatedAt });
        }
      } catch (error) {
        if (!cancelled) toast({ title: 'Replay frame load failed', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
      } finally {
        if (!cancelled) setLoadingFrame(false);
      }
    };
    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameIndex, mode, customerId, orderedFrames]);

  const allAttackEvents = useMemo(() => replayEvents(frame), [frame]);
  const attackEvents = useMemo(() => threatOnly ? allAttackEvents.filter((event) => event.severity === 'high' || event.severity === 'critical' || event.kind === 'threat_seen') : allAttackEvents, [allAttackEvents, threatOnly]);
  useEffect(() => {
    if (!attackEvents.length) { setAttackIndex(0); return; }
    setAttackIndex((index) => Math.max(0, Math.min(index, attackEvents.length - 1)));
  }, [attackEvents.length]);

  const currentEvent = mode === 'attack' ? attackEvents[attackIndex] ?? null : null;
  const caseEvents = useMemo(() => aiCaseEvents(frame, aiOverlay), [frame, aiOverlay]);
  const selectedAiEvent = selectedAiIndex === null ? null : caseEvents[selectedAiIndex] ?? null;
  const diff = useMemo(() => diffSnapshots(frame, previousFrame), [frame, previousFrame]);

  const trail = useMemo(() => {
    const nodeKeys = new Set<string>();
    const edgeKeys = new Set<string>();
    if (mode === 'attack' && attackEvents.length) {
      const start = Math.max(0, attackIndex - 18);
      for (let index = start; index <= attackIndex; index += 1) {
        const event = attackEvents[index];
        event?.nodeKeys.forEach((key) => nodeKeys.add(key));
        event?.edgeKeys.forEach((key) => edgeKeys.add(key));
      }
    }
    selectedAiEvent?.nodeKeys.forEach((key) => nodeKeys.add(key));
    return { nodeKeys, edgeKeys };
  }, [mode, attackEvents, attackIndex, selectedAiEvent]);

  const priority = useMemo(() => {
    const result = new Set<string>();
    trail.nodeKeys.forEach((key) => result.add(key));
    diff?.newNodes.forEach((key) => result.add(key));
    diff?.changedNodes.forEach((key) => result.add(key));
    return result;
  }, [trail, diff]);
  const graph = useMemo(() => layout(frame, priority), [frame, priority]);
  const nodeByKey = useMemo(() => new globalThis.Map(graph.nodes.map((node) => [node.key, node])), [graph.nodes]);
  const selectedNode = frame.nodes.find((node) => node.key === selectedNodeKey) ?? null;

  const cursorMs = currentEvent?.timeMs ?? null;
  const futureNode = (node: MobNode) => {
    if (mode !== 'attack' || cursorMs === null) return false;
    const first = parseTime(node.firstSeen);
    return first !== null && first > cursorMs;
  };
  const expiredNode = (node: MobNode) => {
    if (mode !== 'attack' || cursorMs === null) return false;
    const last = parseTime(node.lastSeen);
    return last !== null && last < cursorMs;
  };

  useEffect(() => {
    if (!playing) return;
    const intervalMs = Math.max(220, Math.round(1500 / speed));
    const timer = window.setInterval(() => {
      if (mode === 'architecture') {
        if (!canSnapshots || frameIndex >= orderedFrames.length - 1) { setPlaying(false); return; }
        setFrameIndex((index) => Math.min(orderedFrames.length - 1, index + 1));
      } else {
        if (!canAttack || attackIndex >= attackEvents.length - 1) { setPlaying(false); return; }
        setAttackIndex((index) => Math.min(attackEvents.length - 1, index + 1));
      }
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [playing, speed, mode, canSnapshots, canAttack, frameIndex, orderedFrames.length, attackIndex, attackEvents.length]);

  function togglePlay() {
    if ((mode === 'architecture' && !canSnapshots) || (mode === 'attack' && !canAttack)) return;
    const next = !playing;
    setPlaying(next);
    if (next) void audit(mode === 'architecture' ? 'mob_snapshot_replay_started' : 'mob_attack_replay_started', { mode, speed, frameIndex, attackIndex });
  }

  function changeMode(next: ReplayMode) {
    setPlaying(false);
    setMode(next);
    setSelectedAiIndex(null);
    setAttackIndex(0);
    void audit('mob_replay_mode_changed', { mode: next });
  }

  function selectAttack(index: number) {
    setAttackIndex(index);
    setPlaying(false);
    const event = attackEvents[index];
    if (event) void audit('mob_attack_replay_event_selected', { eventId: event.id, timestamp: event.timestamp, kind: event.kind });
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement === cardRef.current) await document.exitFullscreen();
      else await cardRef.current?.requestFullscreen();
    } catch (error) {
      toast({ title: 'Fullscreen failed', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    }
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    const target = event.target as Element | null;
    if (target?.closest('[data-replay-node="true"]')) return;
    panRef.current = { active: true, pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY, panX: pan.x, panY: pan.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    const state = panRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const viewWidth = WIDTH / zoom;
    const viewHeight = HEIGHT / zoom;
    setPan({
      x: state.panX + (event.clientX - state.clientX) * (viewWidth / Math.max(1, rect.width)),
      y: state.panY + (event.clientY - state.clientY) * (viewHeight / Math.max(1, rect.height)),
    });
  }
  function onPointerUp(event: ReactPointerEvent<SVGSVGElement>) {
    if (panRef.current.pointerId !== event.pointerId) return;
    panRef.current.active = false;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* noop */ }
  }
  function onWheel(event: ReactWheelEvent<SVGSVGElement>) {
    setZoom((value) => Math.max(.5, Math.min(3, Number((value + (event.deltaY > 0 ? -.1 : .1)).toFixed(2)))));
  }

  function exportReplay(format: 'md' | 'json') {
    if (!canExport) return;
    const payload = {
      customerId,
      customerName,
      mode,
      speed,
      frame: { id: frame.id, generatedAt: frame.generatedAt, sourceFobSnapshotId: frame.sourceFobSnapshotId },
      previousFrame: previousFrame ? { id: previousFrame.id, generatedAt: previousFrame.generatedAt } : null,
      diff: diff ? {
        newNodes: [...diff.newNodes], removedNodes: [...diff.removedNodes], changedNodes: [...diff.changedNodes],
        newEdges: [...diff.newEdges], removedEdges: [...diff.removedEdges], changedEdges: [...diff.changedEdges],
      } : null,
      attackCursor: currentEvent,
      aiC2Case: aiOverlay ? { missionId: aiOverlay.missionId, caseRef: aiOverlay.caseRef, timeline: aiOverlay.timeline ?? [] } : null,
    };
    const safeCustomer = customerName.replace(/[^A-Za-z0-9_-]+/g, '-');
    if (format === 'json') {
      downloadText(`KloudSecure-MoB-Replay-${safeCustomer}-${frame.id.slice(0, 8)}.json`, `${JSON.stringify(payload, null, 2)}\n`, 'application/json;charset=utf-8');
    } else {
      const lines = [
        `# KloudSecure MoB Historical Attack Replay`, '',
        `- Customer: ${customerName}`, `- Mode: ${mode}`, `- Snapshot: ${frame.id}`, `- Snapshot time: ${frame.generatedAt ?? 'unknown'}`, `- Source FOB: ${frame.sourceFobSnapshotId}`, '',
        `## Architecture Delta`,
        diff ? `- New entities: ${diff.newNodes.size}\n- Changed entities: ${diff.changedNodes.size}\n- Removed entities: ${diff.removedNodes.size}\n- New relationships: ${diff.newEdges.size}\n- Changed relationships: ${diff.changedEdges.size}\n- Removed relationships: ${diff.removedEdges.size}` : '- No prior frame available.', '',
        `## Attack Replay Cursor`,
        currentEvent ? `- ${currentEvent.timestamp} — ${currentEvent.label}\n- Kind: ${currentEvent.kind}\n- Severity: ${currentEvent.severity}\n- Sources: ${currentEvent.sourceTables.join(', ') || 'none'}` : '- No attack evidence event selected.', '',
        `## AI C2 Case Lane`,
        aiOverlay?.timeline?.length ? aiOverlay.timeline.map((item) => `- ${item.time} — ${item.text}`).join('\n') : '- No AI C2 timeline overlay loaded.', '',
        `> Replay uses persisted MoB snapshots and retained first/last-seen evidence only. It does not rerun customer telemetry queries and does not invent missing relationships.`, '',
      ];
      downloadText(`KloudSecure-MoB-Replay-${safeCustomer}-${frame.id.slice(0, 8)}.md`, lines.join('\n'), 'text/markdown;charset=utf-8');
    }
    void audit('mob_replay_exported', { format, mode, frameId: frame.id, attackEventId: currentEvent?.id ?? null });
    toast({ title: 'MoB replay exported', description: `${format.toUpperCase()} replay evidence package downloaded.` });
  }

  const frameSliderMax = Math.max(0, orderedFrames.length - 1);
  const attackSliderMax = Math.max(0, attackEvents.length - 1);
  const currentAiKeys = new Set(selectedAiEvent?.nodeKeys ?? []);
  const viewWidth = WIDTH / zoom;
  const viewHeight = HEIGHT / zoom;
  const viewX = (WIDTH - viewWidth) / 2 - pan.x;
  const viewY = (HEIGHT - viewHeight) / 2 - pan.y;

  return (
    <section ref={cardRef} className={`overflow-hidden border border-fuchsia-500/25 bg-slate-950 ${fullscreen ? 'fixed inset-0 z-[120] flex flex-col rounded-none' : 'rounded-2xl shadow-[0_0_60px_rgba(217,70,239,.07)]'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-fuchsia-950/20 to-cyan-950/20 px-4 py-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-black text-slate-100"><History size={16} className="text-fuchsia-300" />MoB Historical Architecture &amp; Attack Replay · Stage 2D.2</div>
          <div className="mt-1 text-[10px] text-slate-600">Persisted MoB evidence only · {orderedFrames.length} architecture frame(s) · {allAttackEvents.length} temporal evidence event(s) · no live customer query</div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" onClick={() => setZoom((value) => Math.max(.5, Number((value - .15).toFixed(2))))} className="rounded-lg border border-slate-700 p-2 text-slate-400"><ZoomOut size={14} /></button>
          <button type="button" onClick={() => setZoom((value) => Math.min(3, Number((value + .15).toFixed(2))))} className="rounded-lg border border-slate-700 p-2 text-slate-400"><ZoomIn size={14} /></button>
          <button type="button" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="rounded-lg border border-slate-700 p-2 text-slate-400"><RotateCcw size={14} /></button>
          {canExport && <><button type="button" onClick={() => exportReplay('md')} className="rounded-lg border border-cyan-500/25 bg-cyan-500/8 px-2.5 py-2 text-[9px] font-bold text-cyan-200"><Download size={12} className="mr-1 inline" />MD</button><button type="button" onClick={() => exportReplay('json')} className="rounded-lg border border-slate-700 px-2.5 py-2 text-[9px] font-bold text-slate-300">JSON</button></>}
          <button type="button" onClick={() => void toggleFullscreen()} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-2 text-[10px] font-black text-fuchsia-100">{fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}{fullscreen ? 'Exit' : 'Maximize'}</button>
          <button type="button" onClick={onExit} className="rounded-lg border border-slate-700 p-2 text-slate-400" title="Return to live MoB"><X size={14} /></button>
        </div>
      </div>

      <div className="border-b border-slate-800 bg-slate-900/45 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-slate-700 bg-slate-950 p-0.5">
            {canSnapshots && <button type="button" onClick={() => changeMode('architecture')} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[9px] font-black ${mode === 'architecture' ? 'bg-cyan-500 text-slate-950' : 'text-slate-500'}`}><Layers3 size={12} />Architecture History</button>}
            {canAttack && <button type="button" onClick={() => changeMode('attack')} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[9px] font-black ${mode === 'attack' ? 'bg-fuchsia-500 text-white' : 'text-slate-500'}`}><Activity size={12} />Attack Replay</button>}
          </div>
          <button type="button" onClick={() => mode === 'architecture' ? setFrameIndex((index) => Math.max(0, index - 1)) : selectAttack(Math.max(0, attackIndex - 1))} className="rounded-lg border border-slate-700 p-2 text-slate-400"><SkipBack size={13} /></button>
          <button type="button" onClick={togglePlay} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[9px] font-black ${playing ? 'border-amber-400/30 bg-amber-500/10 text-amber-200' : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'}`}>{playing ? <Pause size={13} /> : <Play size={13} />}{playing ? 'Pause' : 'Play'}</button>
          <button type="button" onClick={() => mode === 'architecture' ? setFrameIndex((index) => Math.min(frameSliderMax, index + 1)) : selectAttack(Math.min(attackSliderMax, attackIndex + 1))} className="rounded-lg border border-slate-700 p-2 text-slate-400"><SkipForward size={13} /></button>
          <div className="ml-1 flex items-center gap-1"><span className="text-[8px] font-black uppercase text-slate-600">Speed</span>{([0.5,1,2,4] as ReplaySpeed[]).map((value) => <button key={value} type="button" onClick={() => setSpeed(value)} className={`rounded border px-2 py-1 text-[8px] font-black ${speed === value ? 'border-violet-400/35 bg-violet-500/12 text-violet-200' : 'border-slate-700 text-slate-600'}`}>{value}×</button>)}</div>
          {mode === 'attack' && <label className="ml-auto inline-flex items-center gap-2 text-[9px] font-bold text-slate-500"><input type="checkbox" checked={threatOnly} onChange={(event) => { setThreatOnly(event.target.checked); setAttackIndex(0); }} className="accent-fuchsia-500" />Threat-focused events only</label>}
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-[160px_1fr_280px] md:items-center">
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">{mode === 'architecture' ? `Frame ${Math.min(frameIndex + 1, orderedFrames.length)} / ${orderedFrames.length}` : `Event ${Math.min(attackIndex + 1, attackEvents.length)} / ${attackEvents.length}`}</div>
          <input type="range" min={0} max={mode === 'architecture' ? frameSliderMax : attackSliderMax} value={mode === 'architecture' ? frameIndex : attackIndex} onChange={(event) => mode === 'architecture' ? setFrameIndex(Number(event.target.value)) : selectAttack(Number(event.target.value))} className="w-full accent-fuchsia-500" />
          <div className="truncate text-right text-[9px] font-mono text-slate-400">{mode === 'architecture' ? formatTime(frame.generatedAt) : currentEvent ? formatTime(currentEvent.timestamp) : 'No temporal evidence'}</div>
        </div>
      </div>

      {diff && mode === 'architecture' && <div className="flex flex-wrap items-center gap-3 border-b border-emerald-500/15 bg-emerald-950/15 px-4 py-2 text-[9px]"><span className="font-black uppercase tracking-wider text-emerald-200">Architecture frame delta</span><span className="text-emerald-300">+{diff.newNodes.size} entities</span><span className="text-amber-300">∆{diff.changedNodes.size} changed</span><span className="text-rose-300">−{diff.removedNodes.size} removed</span><span className="text-cyan-300">+{diff.newEdges.size} paths</span><span className="text-violet-300">∆{diff.changedEdges.size} path changes</span><span className="text-slate-500">−{diff.removedEdges.size} paths</span></div>}
      {currentEvent && mode === 'attack' && <div className={`flex flex-wrap items-center gap-3 border-b px-4 py-2 text-[9px] ${currentEvent.severity === 'critical' ? 'border-rose-500/20 bg-rose-950/20' : currentEvent.severity === 'high' ? 'border-orange-500/20 bg-orange-950/15' : 'border-cyan-500/15 bg-cyan-950/10'}`}><span className="font-black uppercase tracking-wider text-fuchsia-200"><Clock3 size={11} className="mr-1 inline" />Replay cursor</span><span className="font-mono text-slate-300">{formatTime(currentEvent.timestamp)}</span><span className="font-semibold text-slate-300">{currentEvent.label}</span><span className="ml-auto rounded border border-slate-700 px-2 py-0.5 uppercase text-slate-500">{currentEvent.kind.replaceAll('_',' ')}</span></div>}

      <div className={`grid min-h-0 ${fullscreen ? 'flex-1' : ''} xl:grid-cols-[minmax(0,1fr)_350px]`}>
        <div className={`relative overflow-hidden bg-[radial-gradient(circle_at_50%_35%,rgba(91,33,182,.15),transparent_30%),linear-gradient(#020617,#040b19)] ${fullscreen ? 'min-h-0' : 'min-h-[650px]'}`}>
          {loadingFrame && <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/70 text-xs font-bold text-cyan-200 backdrop-blur-sm">Loading persisted MoB replay frame…</div>}
          <svg width="100%" height={fullscreen ? '100%' : 650} viewBox={`${viewX} ${viewY} ${viewWidth} ${viewHeight}`} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onWheel={onWheel} style={{ touchAction: 'none', cursor: panRef.current.active ? 'grabbing' : 'grab' }}>
            <defs>
              <filter id="mobReplayGlow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="4" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              <marker id="mobReplayArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(103,232,249,.65)" /></marker>
              <pattern id="mobReplayGrid" width="28" height="28" patternUnits="userSpaceOnUse"><path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(148,163,184,.045)" strokeWidth="1"/></pattern>
            </defs>
            <rect x="0" y="0" width={WIDTH} height={HEIGHT} fill="url(#mobReplayGrid)" />
            {graph.zones.map((zone) => <g key={zone.id}><rect x={zone.x} y={zone.y} width={zone.width} height={zone.height} rx="22" fill="rgba(15,23,42,.40)" stroke="rgba(139,92,246,.20)" strokeDasharray="4 7"/><text x={zone.x + 18} y={zone.y + 28} fill="rgb(226,232,240)" fontSize="12" fontWeight="900">{zone.label.toUpperCase()}</text></g>)}
            {graph.edges.map((edge) => {
              const from = nodeByKey.get(edge.from); const to = nodeByKey.get(edge.to); if (!from || !to) return null;
              const current = trail.edgeKeys.has(edge.key) || currentEvent?.edgeKeys.includes(edge.key);
              const fresh = diff?.newEdges.has(edge.key) ?? false;
              const changed = diff?.changedEdges.has(edge.key) ?? false;
              const first = parseTime(edge.firstSeen); const future = mode === 'attack' && cursorMs !== null && first !== null && first > cursorMs;
              const path = curvedPath(from, to);
              const stroke = current ? 'rgba(244,114,182,.98)' : fresh ? 'rgba(52,211,153,.90)' : changed ? 'rgba(251,191,36,.78)' : 'rgba(56,189,248,.32)';
              const width = current ? 3.4 : fresh || changed ? 2.2 : 1.2;
              return <g key={edge.key} opacity={future ? .05 : 1}><path d={path} fill="none" stroke={current ? 'rgba(244,114,182,.12)' : 'rgba(34,211,238,.04)'} strokeWidth={width + 7} strokeLinecap="round"/><path d={path} fill="none" stroke={stroke} strokeWidth={width} strokeDasharray={edge.provenance === 'declared' ? '2 7' : edge.provenance === 'derived' ? '7 6' : undefined} markerEnd="url(#mobReplayArrow)"/><path d={path} fill="none" stroke={current ? 'rgba(255,255,255,.9)' : 'rgba(103,232,249,.42)'} strokeWidth={Math.max(1.1,width*.55)} strokeDasharray="2 14"><animate attributeName="stroke-dashoffset" values="0;-72" dur={current ? '.9s' : '3.4s'} repeatCount="indefinite" /></path></g>;
            })}
            {graph.nodes.map((node) => {
              const fresh = diff?.newNodes.has(node.key) ?? false;
              const changed = diff?.changedNodes.has(node.key) ?? false;
              const current = currentEvent?.nodeKeys.includes(node.key) || currentAiKeys.has(node.key);
              const onTrail = trail.nodeKeys.has(node.key);
              const future = futureNode(node);
              const expired = expiredNode(node);
              const selected = selectedNodeKey === node.key;
              const opacity = future ? .06 : expired ? .18 : 1;
              const stroke = current ? 'rgba(244,114,182,.99)' : fresh ? 'rgba(52,211,153,.96)' : changed ? 'rgba(251,191,36,.96)' : node.crownJewel ? 'rgba(250,204,21,.92)' : node.risk.score >= 85 ? 'rgba(251,113,133,.88)' : 'rgba(56,189,248,.38)';
              return <g key={node.key} data-replay-node="true" transform={`translate(${node.x-node.width/2} ${node.y-node.height/2})`} opacity={opacity} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setSelectedNodeKey(node.key); }} style={{ cursor: 'pointer' }} filter={current || onTrail || fresh || selected ? 'url(#mobReplayGlow)' : undefined}>
                {(current || onTrail) && <rect x="-6" y="-6" width={node.width+12} height={node.height+12} rx="15" fill="rgba(217,70,239,.035)" stroke={current ? 'rgba(244,114,182,.98)' : 'rgba(217,70,239,.52)'} strokeWidth={current ? 2.8 : 1.4} strokeDasharray="5 4"><animate attributeName="stroke-dashoffset" values="0;-36" dur={current ? '.8s' : '1.8s'} repeatCount="indefinite" /></rect>}
                <rect width={node.width} height={node.height} rx="11" fill={node.threatState === 'active_threat' ? 'rgba(76,5,25,.68)' : node.risk.score >= 65 ? 'rgba(67,31,15,.58)' : 'rgba(10,20,40,.95)'} stroke={selected ? 'rgb(34,211,238)' : stroke} strokeWidth={selected ? 2.8 : fresh || changed || node.crownJewel ? 2 : 1.2}/>
                <text x="9" y="14" fill="rgb(100,116,139)" fontSize="7.2" fontWeight="800">{node.type.replaceAll('_',' ').toUpperCase().slice(0,18)}</text>
                <text x="9" y="30" fill="rgb(226,232,240)" fontSize="9.3" fontWeight="800">{node.label.length > 20 ? `${node.label.slice(0,19)}…` : node.label}</text>
                <text x="9" y={node.height-8} fill={node.monitoring.status === 'blind' ? 'rgb(251,113,133)' : node.monitoring.status === 'partial' ? 'rgb(251,191,36)' : 'rgb(52,211,153)'} fontSize="6.8" fontWeight="800">{node.monitoring.status === 'not_applicable' ? 'ARCH' : node.monitoring.status.toUpperCase()}</text>
                <text x={node.width-9} y="14" textAnchor="end" fill={node.risk.score >= 65 ? 'rgb(253,186,116)' : 'rgb(148,163,184)'} fontSize="8" fontWeight="900">R{node.risk.score}</text>
                {fresh && <text x={node.width-9} y="28" textAnchor="end" fill="rgb(52,211,153)" fontSize="6.4" fontWeight="900">NEW</text>}
                {changed && !fresh && <text x={node.width-9} y="28" textAnchor="end" fill="rgb(251,191,36)" fontSize="6.4" fontWeight="900">CHANGED</text>}
              </g>;
            })}
          </svg>
          <div className="pointer-events-none absolute bottom-4 left-4 rounded-lg border border-white/10 bg-slate-950/85 px-3 py-2 text-[10px] text-slate-500 backdrop-blur">{mode === 'architecture' ? `Historical frame ${frameIndex+1}/${orderedFrames.length}` : `Attack evidence ${attackIndex+1}/${attackEvents.length || 0}`} · rendered {graph.nodes.length} nodes / {graph.edges.length} paths · zoom {Math.round(zoom*100)}%</div>
          <div className="pointer-events-none absolute bottom-4 right-4 rounded-lg border border-fuchsia-400/20 bg-fuchsia-950/45 px-3 py-2 text-[9px] font-black uppercase tracking-[.16em] text-fuchsia-200 backdrop-blur">MoB 2D.2 · persisted evidence replay</div>
        </div>

        <aside className={`border-l border-slate-800 bg-slate-950/96 p-4 ${fullscreen ? 'min-h-0 overflow-y-auto' : ''}`}>
          <div className="space-y-4">
            <div className="rounded-xl border border-fuchsia-500/20 bg-fuchsia-950/10 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-fuchsia-300">Replay state</div><div className="mt-2 grid grid-cols-2 gap-2 text-[9px]"><ReplayMini label="Mode" value={mode === 'architecture' ? 'Architecture history' : 'Attack evidence'} /><ReplayMini label="Speed" value={`${speed}×`} /><ReplayMini label="Frame" value={`${frameIndex+1}/${orderedFrames.length}`} /><ReplayMini label="Generated" value={formatTime(frame.generatedAt)} /></div></div>
            {selectedNode && <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="flex items-start justify-between gap-2"><div><div className="text-[8px] font-black uppercase text-cyan-300">Selected entity</div><div className="mt-1 break-words text-xs font-black text-slate-100">{selectedNode.label}</div></div><span className={`text-[9px] font-black ${riskClass(selectedNode.risk.score)}`}>R{selectedNode.risk.score}</span></div><div className="mt-2 text-[9px] text-slate-500">First seen: {formatTime(selectedNode.firstSeen)}<br/>Last seen: {formatTime(selectedNode.lastSeen)}<br/>Threat: {selectedNode.threatState.replaceAll('_',' ')}<br/>Sources: {selectedNode.sourceTables.join(', ') || 'none'}</div></div>}
            {canEvidence && mode === 'attack' && <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="flex items-center justify-between"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Evidence event lane</div><Clock3 size={12} className="text-cyan-300" /></div><div className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1">{attackEvents.slice(Math.max(0,attackIndex-5), Math.min(attackEvents.length,attackIndex+6)).map((event) => { const index = attackEvents.indexOf(event); return <button key={event.id} type="button" onClick={() => selectAttack(index)} className={`w-full rounded-lg border px-2.5 py-2 text-left ${index === attackIndex ? 'border-fuchsia-400/35 bg-fuchsia-500/10' : 'border-slate-800 bg-slate-950/40'}`}><div className="flex items-center justify-between gap-2"><span className="font-mono text-[8px] text-slate-500">{formatTime(event.timestamp)}</span><span className={`text-[7px] font-black uppercase ${event.severity === 'critical' ? 'text-rose-300' : event.severity === 'high' ? 'text-orange-300' : 'text-slate-600'}`}>{event.severity}</span></div><div className="mt-1 text-[9px] leading-4 text-slate-300">{event.label}</div></button>; })}{!attackEvents.length && <div className="py-5 text-center text-[9px] text-slate-600">No first/last-seen temporal evidence is retained in this snapshot.</div>}</div></div>}
            {canAiLane && aiOverlay?.timeline?.length ? <div className="rounded-xl border border-violet-500/20 bg-violet-950/10 p-3"><div className="flex items-center justify-between"><div><div className="text-[9px] font-black uppercase tracking-wider text-violet-300"><Radar size={10} className="mr-1 inline" />AI C2 case-relative timeline</div><div className="mt-1 text-[8px] text-slate-600">{aiOverlay.caseRef} · case timestamps are retained exactly as adjudicated; MoB does not invent missing calendar dates.</div></div></div><div className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">{caseEvents.map((event,index) => <button key={`${event.time}-${index}`} type="button" onClick={() => { setSelectedAiIndex(index); setPlaying(false); }} className={`w-full rounded-lg border px-2.5 py-2 text-left ${selectedAiIndex === index ? 'border-violet-400/35 bg-violet-500/10' : 'border-slate-800 bg-slate-950/40'}`}><div className="flex items-center justify-between"><span className="font-mono text-[8px] text-violet-300">{event.time}</span><span className="text-[7px] font-black uppercase text-slate-600">{event.category}</span></div><div className="mt-1 text-[9px] leading-4 text-slate-300">{event.text}</div><div className="mt-1 text-[7px] text-slate-600">{event.nodeKeys.length} MoB entity match(es)</div></button>)}</div></div> : null}
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-[9px] leading-5 text-slate-500"><ShieldCheck size={11} className="mr-1 inline text-emerald-300"/><b className="text-slate-300">Evidence boundary:</b> replay uses persisted MoB snapshots plus retained node/relationship first/last-seen timestamps. AI C2 case-relative events remain separately labelled when their absolute calendar date is not known. No missing path or timestamp is synthesized.</div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function ReplayMini({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border border-slate-800 bg-slate-950/55 p-2"><div className="text-[7px] font-bold uppercase tracking-wider text-slate-600">{label}</div><div className="mt-1 truncate text-[9px] font-semibold text-slate-300" title={value}>{value}</div></div>;
}
